/**
 * PATH: packages/email-hub/src/feature/components/filter-sidebar/CategorySection.tsx
 * FILE_ID: EMAILHUB-FILTERSIDEBAR-CATEGORY-001
 * PURPOSE: Renders category filter choices and facet counts.
 */
import { useMemo } from 'react'

import { cn } from '../../../_internal/lib/utils'
import { SectionHeader } from './SidebarChrome'
import type { CategorySidebarConfig, SectionProps } from './types'

interface CategorySectionProps extends SectionProps {
  categories: CategorySidebarConfig[]
  selected: string | undefined
  onSelect: (key: string | undefined) => void
  counts?: Array<{ key: string; count: number }>
}

export function CategorySection({
  expanded,
  onToggle,
  categories,
  selected,
  onSelect,
  counts,
}: CategorySectionProps) {
  const countMap = useMemo(() => {
    const m: Record<string, number> = {}
    for (const c of counts || []) m[c.key] = c.count
    return m
  }, [counts])

  return (
    <div className="mb-3">
      <SectionHeader label="Category" expanded={expanded} onToggle={onToggle} />
      {expanded && (
        <div className="space-y-0.5">
          <button
            onClick={() => onSelect(undefined)}
            className={cn(
              'w-full text-left px-2 py-1 text-xs rounded-md transition-colors',
              !selected
                ? 'bg-primary/10 text-primary font-medium'
                : 'text-muted-foreground hover:bg-accent',
            )}
          >
            All Categories
          </button>
          {categories.map((cfg) => {
            const Icon = cfg.icon
            const count = countMap[cfg.key]
            return (
              <button
                key={cfg.key}
                onClick={() => onSelect(cfg.key)}
                className={cn(
                  'w-full text-left px-2 py-1 text-xs rounded-md transition-colors flex items-center gap-1.5',
                  selected === cfg.key
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-muted-foreground hover:bg-accent',
                )}
              >
                <Icon className="h-3 w-3" />
                <span className="flex-1 truncate">{cfg.label}</span>
                {count !== undefined && count > 0 && (
                  <span className="text-[9px] tabular-nums opacity-70">{count}</span>
                )}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
